import {ReactNode, useEffect, useRef} from 'react';

import {Observability} from '@stripe-internal/conversations-client';

import {AppCloseHandler, AppConfig, AppProfile} from '../types';
import {AppContext} from '../contexts/AppContext';
import {AppImpl} from '../App';
import {ClientFetch, defaultClientFetch} from '../lib/util/clientFetch';
import {ConversationsUIProviders} from './ConversationsUIProviders';
import {ParentMessageBus} from '../lib/parentMessageBus/ParentMessageBus';

type Props = {
  config: AppConfig;
  profile: AppProfile;
  clientFetch?: ClientFetch;
  parentMessageBus?: ParentMessageBus;
  observability?: Partial<Observability>;
  children: ReactNode;
  onClose?: AppCloseHandler;
};

export function AppProvider({
  config,
  profile,
  clientFetch = defaultClientFetch(),
  parentMessageBus,
  observability,
  onClose,
  children,
}: Props) {
  const appRef = useRef<AppImpl | null>(null);
  if (
    !appRef.current ||
    appRef.current.config.audience !== config.audience ||
    appRef.current.config.entryPoint !== config.entryPoint ||
    appRef.current.config.triggerPoint !== config.triggerPoint
  ) {
    // in the scenario where we had an existing instance, but the configuration has changed, unsubscribe
    appRef.current?.unsubscribeFromParentMessageBus();

    appRef.current = new AppImpl(
      config,
      profile,
      clientFetch,
      parentMessageBus,
      onClose,
      observability,
    );
  }

  useEffect(() => {
    appRef.current?.subscribeToParentMessageBus();
    return () => appRef.current?.unsubscribeFromParentMessageBus();
  }, []);

  return (
    <AppContext.Provider value={appRef.current}>
      <ConversationsUIProviders>{children}</ConversationsUIProviders>
    </AppContext.Provider>
  );
}
